import { motion } from "framer-motion";
import axios from "axios";
import PullToRefresh from "react-simple-pull-to-refresh";
import { Loader2, RefreshCw, Settings, X } from "lucide-react";
import { lazy, Suspense, useState } from "react";

import DashboardCustomizer from "../DashboardCustomizer";
import SevereAlertsBanner from "../SevereAlertsBanner";
import VerseOfTheDay from "../VerseOfTheDay";
import { DiscountCountdown, OnboardingModal, TrialEndingBanner, WhatYoureMissing } from "../PremiumFeatures";
import { AccuracyInsightsSection } from "./AccuracyInsightsSection";
import { CurrentWeatherCard } from "./CurrentWeatherCard";
import { DashboardFooter } from "./DashboardFooter";
import { DashboardHeader } from "./DashboardHeader";
import { DashboardTopCards } from "./DashboardTopCards";
import { ReorderableWidgets } from "./ReorderableWidgets";
import { SavedLocationsStrip } from "./SavedLocationsStrip";
import { ForecastSourcePreference } from "../ForecastSourcePreference";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JetRunMiniGame = lazy(() => import("../TroubleBoyRunMiniGame").then((module) => ({ default: module.JetRunMiniGame })));

export function DashboardLayout(props) {
  const {
    AccuracyItem,
    DashboardContainer,
    HuntingWidget,
    LocalTimeCard,
    WeatherIcon,
    aiSummary,
    completeOnboarding,
    convertTemp,
    dashboardWidgets,
    discountEndDate,
    extendedForecast,
    fetchWeather,
    forecast,
    formatTime,
    generateAiSummary,
    getBackgroundImage,
    handleRefresh,
    handleStartTrial,
    healthData,
    isCelsius,
    isDarkMode,
    isLocationSaved,
    isPremium,
    isSearching,
    isTrial,
    isWidgetVisible,
    loading,
    loadingAi,
    location,
    logout,
    moonData,
    removeLocation,
    requestGPSLocation,
    saveLocation,
    savedLocations,
    searchQuery,
    searchResults,
    selectLocation,
    severeAlerts,
    setAiSummary,
    setDashboardWidgets,
    setExtendedForecast,
    setForecast,
    setLocation,
    setSearchQuery,
    setShowDashboardCustomizer,
    setWeather,
    showDashboardCustomizer,
    showOnboarding,
    tempSymbol,
    tempUnit,
    token,
    toggleTempUnit,
    toggleTheme,
    trialDaysLeft,
    user,
    weather,
  } = props;
  const container = DashboardContainer;
  const item = AccuracyItem;
  const [showJetRunJax, setShowJetRunJax] = useState(false);

  return (
    <div className="min-h-screen relative transition-colors duration-300">
      {/* Onboarding Modal for First-Time Users */}
      <OnboardingModal 
        isOpen={showOnboarding} 
        onClose={completeOnboarding}
        onStartTrial={handleStartTrial}
      />

      {/* Dynamic Background */}
      <div 
        className={`fixed inset-0 bg-cover bg-center transition-all duration-500 ${!isDarkMode ? 'opacity-75' : 'opacity-100'}`}
        style={{ backgroundImage: `url('${getBackgroundImage()}')` }}
      />
      <div className={`fixed inset-0 transition-colors duration-300 ${isDarkMode ? 'bg-black/60' : 'bg-gradient-to-br from-slate-100/55 via-gray-100/45 to-slate-200/55'}`} />

      {/* Content with Pull-to-Refresh */}
      <PullToRefresh
        onRefresh={handleRefresh}
        pullingContent={
          <div className="flex items-center justify-center py-4 text-white/60">
            <RefreshCw className="w-5 h-5 mr-2" />
            <span className="text-sm">Pull to refresh</span>
          </div>
        }
        refreshingContent={
          <div className="flex items-center justify-center py-4 text-[#00f2ea]">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            <span className="text-sm">Refreshing...</span>
          </div>
        }
        resistance={2.5}
        maxPullDownDistance={120}
        pullDownThreshold={80}
      >
        <div className="dashboard-shell relative z-10 min-h-screen px-4 pb-4 md:px-8 md:pb-8 lg:px-12 lg:pb-12">
        {/* Trial Ending Banner */}
        {isTrial && trialDaysLeft <= 3 && (
          <div className="mb-4">
            <TrialEndingBanner daysLeft={trialDaysLeft} />
          </div>
        )}

        {/* Discount Countdown Banner (for non-premium users) */}
        {!isPremium && (
          <div className="mb-4">
            <DiscountCountdown endDate={discountEndDate} />
          </div>
        )}

        {/* Header */}
        <DashboardHeader
          isDarkMode={isDarkMode}
          isPremium={isPremium}
          location={location}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          isSearching={isSearching}
          searchResults={searchResults}
          selectLocation={selectLocation}
          requestGPSLocation={requestGPSLocation}
          toggleTempUnit={toggleTempUnit}
          isCelsius={isCelsius}
          toggleTheme={toggleTheme}
          token={token}
          weather={weather}
          severeAlerts={severeAlerts}
          tempSymbol={tempSymbol}
          convertTemp={convertTemp}
          setShowDashboardCustomizer={setShowDashboardCustomizer}
          user={user}
          logout={logout}
          onOpenJetRunJax={() => setShowJetRunJax(true)}
        />

        {/* Verse of the Day */}
        {isWidgetVisible('verse') && <VerseOfTheDay />}

        {/* What You're Missing - For Free Users */}
        {!isPremium && !isTrial && (
          <motion.div 
            variants={item}
            className="mb-6"
          >
            <WhatYoureMissing />
          </motion.div>
        )}

        {/* Saved Locations */}
        <SavedLocationsStrip
          savedLocations={savedLocations}
          setLocation={setLocation}
          setAiSummary={setAiSummary}
          removeLocation={removeLocation}
        />

        <ForecastSourcePreference location={location} isDarkMode={isDarkMode} />

        {/* Severe Weather Alerts Banner */}
        {isWidgetVisible('alerts') && severeAlerts && severeAlerts.length > 0 && (
          <SevereAlertsBanner alerts={severeAlerts} />
        )}

        {/* Main Content - Bento Grid */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6 auto-rows-[minmax(120px,auto)]"
        >
          {/* Current Weather - Hero Card */}
          <CurrentWeatherCard
            item={item}
            location={location}
            weather={weather}
            fetchWeather={fetchWeather}
            loading={loading}
            isLocationSaved={isLocationSaved}
            saveLocation={saveLocation}
            WeatherIcon={WeatherIcon}
            convertTemp={convertTemp}
          />

          {/* Top dashboard cards */}
          <DashboardTopCards
            item={item}
            isWidgetVisible={isWidgetVisible}
            isPremium={isPremium}
            location={location}
            weather={weather}
            forecast={forecast}
            healthData={healthData}
            aiSummary={aiSummary}
            generateAiSummary={generateAiSummary}
            loadingAi={loadingAi}
            LocalTimeCard={LocalTimeCard}
            convertTemp={convertTemp}
            tempSymbol={tempSymbol}
          />

          {/* Accuracy insights and premium charts */}
          <AccuracyInsightsSection
            item={item}
            weather={weather}
            location={location}
            isPremium={isPremium}
            isWidgetVisible={isWidgetVisible}
            isDarkMode={isDarkMode}
          />
        </motion.div>

        {/* Reorderable Widgets Section */}
        {/* Reorderable Widgets Section */}
        <ReorderableWidgets
          dashboardWidgets={dashboardWidgets}
          isWidgetVisible={isWidgetVisible}
          forecast={forecast}
          extendedForecast={extendedForecast}
          weather={weather}
          tempUnit={tempUnit}
          isPremium={isPremium}
          location={location}
          token={token}
          isDarkMode={isDarkMode}
          savedLocations={savedLocations}
          moonData={moonData}
          formatTime={formatTime}
          HuntingWidget={HuntingWidget}
          setLocation={setLocation}
          setWeather={setWeather}
          setForecast={setForecast}
          setExtendedForecast={setExtendedForecast}
        />

        {/* Footer */}
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
      </PullToRefresh>

      {/* Dashboard Customizer Modal */}
      {isPremium && (
        <button
          type="button"
          onClick={() => setShowDashboardCustomizer(true)}
          className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[#00f2ea] text-black shadow-2xl shadow-cyan-500/30 transition-transform hover:scale-105 active:scale-95"
          data-testid="dashboard-customizer-open-button"
          aria-label="Customize dashboard"
        >
          <Settings className="h-5 w-5" />
        </button>
      )}

      {/* Dashboard Customizer Modal */}
      {isPremium && (
        <DashboardCustomizer
          isOpen={showDashboardCustomizer}
          onClose={() => setShowDashboardCustomizer(false)}
          widgets={dashboardWidgets}
          onSave={(widgets) => {
            setDashboardWidgets(widgets);
            // Save to backend
            if (token) {
              axios.put(`${API}/user/dashboard-config`, { widgets }, {
                headers: { Authorization: `Bearer ${token}` }
              }).catch(err => console.error("Failed to save dashboard config:", err));
            }
          }}
          isDarkMode={isDarkMode}
        />
      )}

      {showJetRunJax && (
        <div className="fixed inset-0 z-[100] flex bg-black" data-testid="jet-run-jax-fullscreen-overlay">
          <button
            type="button"
            onClick={() => setShowJetRunJax(false)}
            className="absolute right-3 top-3 z-[110] flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white shadow-2xl backdrop-blur transition-transform hover:scale-105 active:scale-95"
            data-testid="jet-run-jax-close-button"
            aria-label="Close Jet Run Jax"
            title="Close Jet Run Jax"
          >
            <X className="h-5 w-5" />
          </button>
          <Suspense
            fallback={
              <div className="flex h-full w-full items-center justify-center bg-sky-400 text-white" data-testid="jet-run-jax-loading-state">
                <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                <span className="text-sm font-black uppercase tracking-wide">Loading Jet Run Jax</span>
              </div>
            }
          >
            <JetRunMiniGame enableLeaderboard={true} />
          </Suspense>
        </div>
      )}
    </div>
  );
}
